import { useCallback, useEffect, useMemo, useRef, useState } from 'preact/hooks'
import { useOmneoClient } from 'libs/omneo/hooks/useOmneoClient'
import { useSearchQuery } from 'libs/omneo/hooks/useSearchQuery'
import {
  createProfile,
  getAllWishlists,
  invalidateWishlistCache,
  readCachedWishlists
} from 'libs/omneo/helpers/getAllWishlists'
import { applyOperations, createList } from 'libs/omneo/wishlist/wishlistRepository.mjs'
import { dispatchWishlistUpdate, toWishlistActions } from 'libs/omneo/wishlist/wishlistEvents.mjs'
import {
  diffDraft,
  hasChanges,
  seedDraft,
  setActive,
  setSize,
  toggleList
} from 'libs/omneo/wishlist/wishlistDraft.mjs'

const EMPTY_DRAFT = { entries: {}, activeListId: null }

const holdsProduct = (lists, productId) =>
  lists.some((list) => (list.items ?? []).some((item) => String(item?.meta?.productId) === String(productId)))

/**
 * Owns the drawer's data: list reads, the draft, and the submit diff. Kept out of
 * WishlistDrawer.js so the shell stays about portal/focus/scroll mechanics.
 */
export const useWishlistDrawer = ({ isOpen, hasOpenedOnce, isLoggedIn, productId, productImage, variants, fallbackVariantId }) => {
  const { client, error: clientError, retry: retryClient } = useOmneoClient()
  const [selectedVariantId] = useSearchQuery('variant', fallbackVariantId)

  const [lists, setLists] = useState([])
  const [seeded, setSeeded] = useState(EMPTY_DRAFT)
  const [draft, setDraft] = useState(EMPTY_DRAFT)
  const [isLoading, setIsLoading] = useState(false)
  const [loadError, setLoadError] = useState(null)
  const [isSaving, setIsSaving] = useState(false)
  const [createError, setCreateError] = useState(null)
  const [rowErrors, setRowErrors] = useState({})
  const [isSavedHint, setIsSavedHint] = useState(false)
  const [reloadToken, setReloadToken] = useState(0)

  // Draft is read inside async callbacks that must not re-create on every keystroke.
  const draftRef = useRef(draft)
  draftRef.current = draft

  // Read when seeding, never as an effect dependency: a variant-selected event while the
  // drawer is open would otherwise re-seed and silently discard in-progress toggles.
  const selectedVariantRef = useRef(selectedVariantId)
  selectedVariantRef.current = selectedVariantId

  // A partial write re-reads to resynchronise the seed, and that read must not clear the
  // per-row failures it was triggered by.
  const keepRowErrorsRef = useRef(false)

  const skuFor = useCallback(
    (variantId) => variants.find((variant) => String(variant.id) === String(variantId))?.sku,
    [variants]
  )

  const labelFor = useCallback(
    (variantId) => variants.find((variant) => String(variant.id) === String(variantId))?.name ?? '',
    [variants]
  )

  // Cache-only, so the trigger can show the saved state without the network read,
  // which would create a wishlist for every child profile lacking one.
  useEffect(() => {
    if (!isLoggedIn || hasOpenedOnce) return
    let cancelled = false
    readCachedWishlists().then((cached) => {
      if (!cancelled && cached) setIsSavedHint(holdsProduct(cached, productId))
    })
    return () => {
      cancelled = true
    }
  }, [isLoggedIn, hasOpenedOnce, productId])

  // Re-seeded on every open: the drawer stays mounted, so a one-shot seed would
  // show stale toggles on reopen.
  useEffect(() => {
    if (!isOpen || !isLoggedIn || !client) return
    let cancelled = false

    setIsLoading(true)
    setLoadError(null)
    getAllWishlists(client, false)
      .then((loaded) => {
        if (cancelled) return
        const next = seedDraft({ lists: loaded, productId, selectedVariantId: selectedVariantRef.current })
        setLists(loaded)
        setSeeded(next)
        setDraft(next)
        if (keepRowErrorsRef.current) {
          keepRowErrorsRef.current = false
        } else {
          setRowErrors({})
        }
      })
      .catch((error) => {
        if (cancelled) return
        console.error('Failed to load wishlists:', error)
        setLoadError(error)
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [isOpen, isLoggedIn, client, productId, reloadToken])

  const activeEntry = draft.activeListId == null ? null : draft.entries[draft.activeListId]

  const isSaved = useMemo(
    () => (Object.keys(draft.entries).length ? Object.values(draft.entries).some((entry) => entry.enabled) : isSavedHint),
    [draft.entries, isSavedHint]
  )

  const canSubmit = hasChanges(seeded, draft) && !isSaving && !isLoading

  const clearRowError = (listId) =>
    setRowErrors((errors) => {
      if (!errors[listId]) return errors
      const next = { ...errors }
      delete next[listId]
      return next
    })

  const onToggle = useCallback((listId) => {
    clearRowError(listId)
    setDraft((current) => toggleList(current, listId))
  }, [])

  const onActivate = useCallback((listId) => setDraft((current) => setActive(current, listId)), [])
  const onSize = useCallback((variantId) => {
    setRowErrors({})
    setDraft((current) => setSize(current, variantId))
  }, [])
  // A failed sign-in leaves no client, so re-reading lists alone would retry nothing.
  const onRetry = useCallback(() => {
    if (clientError) retryClient()
    setReloadToken((token) => token + 1)
  }, [clientError, retryClient])

  const onCreate = useCallback(
    async (name) => {
      if (!client) return false
      setCreateError(null)
      setIsSaving(true)
      try {
        const created = await createList({ profileClient: client.profileClient, name })
        if (!created?.id) throw new Error('The list was created without an id')

        const list = { ...created, items: created.items ?? [], profile: null }
        const current = draftRef.current
        setLists((existing) => [...existing, list])
        setSeeded((existing) => ({
          ...existing,
          entries: { ...existing.entries, [list.id]: { enabled: false, variantId: null, quantity: 1, itemId: null } }
        }))
        setDraft({
          activeListId: list.id,
          entries: {
            ...current.entries,
            [list.id]: {
              enabled: true,
              variantId: activeEntry?.variantId ?? selectedVariantId,
              quantity: activeEntry?.quantity ?? 1,
              itemId: null
            }
          }
        })
        return true
      } catch (error) {
        console.error('Failed to create wishlist:', error)
        setCreateError('We could not create that list. Please try again.')
        return false
      } finally {
        setIsSaving(false)
      }
    },
    [client, activeEntry, selectedVariantId]
  )

  const onSubmit = useCallback(
    async (onDone) => {
      if (!client || !canSubmit) return

      // A size is only required where the shopper is actually adding something.
      const missing = Object.entries(draft.entries).filter(
        ([listId, entry]) => entry.enabled && !seeded.entries[listId]?.enabled && !skuFor(entry.variantId)
      )
      if (missing.length) {
        setRowErrors(Object.fromEntries(missing.map(([listId]) => [listId, 'Choose a size first.'])))
        return
      }

      setIsSaving(true)
      try {
        const operations = diffDraft(seeded, draft, { lists, productId, skuFor, img: productImage })
        const applied = await applyOperations({
          profileClient: client.profileClient,
          createProfile,
          operations
        })

        // profileClient.fetchClient resolves with `data: null` instead of throwing on
        // an HTTP error, so applyOperations' `ok` cannot see a rejected BFF write.
        // A successful add always echoes the created item, so a missing id is the tell.
        const failed = applied.filter(
          (operation) => !operation.ok || (operation.type === 'add' && !operation.item?.id)
        )
        const succeeded = applied.filter((operation) => !failed.includes(operation))

        dispatchWishlistUpdate(toWishlistActions(succeeded, lists))
        await invalidateWishlistCache()

        if (failed.length) {
          keepRowErrorsRef.current = true
          setRowErrors(Object.fromEntries(failed.map((operation) => [operation.listId, 'This list did not save.'])))
          // Re-seed, or the next submit would re-issue the operations that did succeed.
          setReloadToken((token) => token + 1)
          return
        }

        onDone()
      } catch (error) {
        console.error('Failed to save wishlist changes:', error)
        setLoadError(error)
      } finally {
        setIsSaving(false)
      }
    },
    [client, canSubmit, draft, seeded, lists, productId, productImage, skuFor]
  )

  return {
    lists,
    draft,
    activeEntry,
    selectedVariantId,
    isLoading: (isLoading || (isLoggedIn && !client)) && !clientError,
    loadError: loadError ?? clientError,
    isSaving,
    createError,
    rowErrors,
    isSaved,
    canSubmit,
    labelFor,
    onToggle,
    onActivate,
    onSize,
    onCreate,
    onSubmit,
    onRetry
  }
}
